import { __awaiter } from "tslib";
import { ccApi } from './ccApi';
import { CC_API_TAGS } from './constants';
/** Path segment for the preview wrapper resource. */
const PREVIEW_WRAPPER_PATH = 'previewWrappers';
/**
 * Decodes the base64-encoded `value` field returned by the backend for a
 * single preview wrapper. Exported for unit testing.
 *
 * Falls back to the raw value when value is absent or invalid base64 to
 * avoid throwing on malformed payloads and preserve backward-compatible behavior.
 */
export const transformPreviewWrapperResponse = (response) => {
    if (!response.value)
        return response;
    try {
        return Object.assign(Object.assign({}, response), { value: atob(response.value) });
    }
    catch (_a) {
        return response;
    }
};
/**
 * RTK Query endpoints for preview wrapper data.
 *
 * This module extends the base ccApi with preview wrapper CRUD endpoints.
 *
 * All endpoints are scoped under `/{tenantId}/previewWrappers`.
 *
 * @see https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
 */
export const previewWrapperApi = ccApi.injectEndpoints({
    endpoints: (builder) => ({
        /**
         * Fetches the full list of preview wrappers for a tenant.
         *
         * @endpoint GET /{tenantId}/previewWrappers
         */
        getPreviewWrappersList: builder.query({
            query: ({ tenantId }) => `/${tenantId}/${PREVIEW_WRAPPER_PATH}`,
            providesTags: [CC_API_TAGS.PreviewWrapperList],
        }),
        /**
         * Fetches a single preview wrapper by ID.
         *
         * The backend encodes the `value` field as base64; `transformResponse`
         * decodes it via `atob` to restore the original string.
         *
         * @endpoint GET /{tenantId}/previewWrappers/{id}
         */
        getPreviewWrapperById: builder.query({
            query: ({ tenantId, id }) => `/${tenantId}/${PREVIEW_WRAPPER_PATH}/${id}`,
            transformResponse: transformPreviewWrapperResponse,
            providesTags: (_result, _error, { id }) => [{ type: CC_API_TAGS.PreviewWrapper, id }],
        }),
        /**
         * Creates a new preview wrapper.
         *
         * @endpoint POST /{tenantId}/previewWrappers
         */
        createPreviewWrapper: builder.mutation({
            query: ({ tenantId, data }) => ({
                url: `/${tenantId}/${PREVIEW_WRAPPER_PATH}`,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [CC_API_TAGS.PreviewWrapperList],
        }),
        /**
         * Updates an existing preview wrapper.
         *
         * @endpoint PUT /{tenantId}/previewWrappers/{id}
         */
        updatePreviewWrapper: builder.mutation({
            query: ({ tenantId, data }) => ({
                url: `/${tenantId}/${PREVIEW_WRAPPER_PATH}/${data.id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_result, _error, { data }) => [
                CC_API_TAGS.PreviewWrapperList,
                { type: CC_API_TAGS.PreviewWrapper, id: data.id },
            ],
        }),
        /**
         * Bulk-deletes preview wrappers by issuing individual DELETE calls.
         *
         * There is no bulk-delete API endpoint, so this uses a custom
         * `queryFn` with a `Promise.allSettled` pattern.
         *
         * @returns `{ succeeded, failed }` so consumers can report per-item errors.
         */
        bulkDeletePreviewWrappers: builder.mutation({
            queryFn(_a, _queryApi_1, _extraOptions_1, baseQuery_1) {
                return __awaiter(this, arguments, void 0, function* ({ tenantId, ids }, _queryApi, _extraOptions, baseQuery) {
                    const results = yield Promise.allSettled(ids.map((id) => Promise.resolve(baseQuery({
                        url: `/${tenantId}/${PREVIEW_WRAPPER_PATH}/${id}`,
                        method: 'DELETE',
                    }))));
                    const succeeded = [];
                    const failed = [];
                    results.forEach((result, index) => {
                        var _a, _b;
                        if (result.status === 'fulfilled' && !result.value.error) {
                            succeeded.push(ids[index]);
                        }
                        else {
                            const errorMessage = result.status === 'rejected'
                                ? String(result.reason)
                                : JSON.stringify((_b = (_a = result.value.error) === null || _a === void 0 ? void 0 : _a.data) !== null && _b !== void 0 ? _b : 'Unknown error');
                            failed.push({ id: ids[index], error: errorMessage });
                        }
                    });
                    return { data: { succeeded, failed } };
                });
            },
            invalidatesTags: [CC_API_TAGS.PreviewWrapperList],
        }),
    }),
});
export const { useBulkDeletePreviewWrappersMutation, useCreatePreviewWrapperMutation, useGetPreviewWrapperByIdQuery, useGetPreviewWrappersListQuery, useUpdatePreviewWrapperMutation, } = previewWrapperApi;
